import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import {
  ContactSchema,
  MAX_MAILBOXES_PER_DOMAIN,
  MailboxDraftSchema,
  NEW_MAILBOX,
  OrderDraftSchema,
  type Contact,
  type DomainSearch,
  type MailboxOrder,
  type OrderDraft,
  type Vendor,
} from "../../../shared/mailboxOrders";
import { useNative } from "../../../shared/phone/media";
import { Segmented } from "../../../shared/phone/Row";
import { Sheet as PnSheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { api } from "../../lib/api";
import { shownError } from "../../lib/connectionsApi";
import { deliveryWords, mailboxOrdersApi, price, priceSentence, sortVendors, vendorTitle } from "../../lib/mailboxOrdersApi";
import { productsApi } from "../../lib/productsApi";
import { COUNTRIES } from "../more/countries";
import { PickList } from "../more/PickList";

/**
 * Buying a sending domain and its mailboxes through Smartlead, in six steps: the kind of mailboxes, the
 * domains, who signs the emails, where the domain leads, the registration contact, and the price with Buy.
 * The server checks the order as a draft before Review shows its price; Buy is the one press that spends.
 */

const STEPS = ["kind", "domains", "mailboxes", "forwarding", "contact", "review"] as const;
type Step = (typeof STEPS)[number];

const MAX_DOMAINS = 3;
const DEFAULT_PER_DOMAIN = 2;

type ContactForm = Record<keyof Contact, string>;
const CONTACT_KEYS: (keyof Contact)[] = ["firstName", "lastName", "email", "company", "country", "state", "city", "addressLineOne", "addressLineTwo", "postalCode", "phoneCc", "phone"];
const EMPTY_CONTACT: ContactForm = { firstName: "", lastName: "", email: "", company: "", country: "", state: "", city: "", addressLineOne: "", addressLineTwo: "", postalCode: "", phoneCc: "+1", phone: "" };

const slug = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** The address a mailbox gets unless the owner types another: nat, nat.wallen, n.wallen. */
function suggestedLocal(first: string, last: string, i: number): string {
  const f = slug(first);
  const l = slug(last);
  const ways = [...new Set([f, f && l ? `${f}.${l}` : "", f && l ? `${f[0]}.${l}` : "", f && l ? `${f}${l}` : ""].filter(Boolean))];
  return ways[i] ?? `${f || "hello"}${i + 1}`;
}

/** A website or address as a bare domain: https://www.flashkut.ai/x becomes www.flashkut.ai. */
const toDomain = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.$/, "");

const LOCAL = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;

function contactErrors(c: ContactForm): Partial<Record<keyof Contact, string>> {
  const r = ContactSchema.safeParse(c);
  if (r.success) return {};
  const out: Partial<Record<keyof Contact, string>> = {};
  for (const issue of r.error.issues) {
    const k = issue.path[0] as keyof Contact;
    if (out[k]) continue;
    if (k === "phoneCc" || k === "phone") out[k] = issue.message;
    else if (k === "email") out[k] = "Use a full email address.";
    else out[k] = "Fill this in.";
  }
  return out;
}

const contactOf = (c: Contact): ContactForm => ({ ...EMPTY_CONTACT, ...c, addressLineTwo: c.addressLineTwo ?? "" });

/** What a draft buys, as the server checked it. */
function OrderFacts({ o }: { o: MailboxOrder }) {
  const kind = vendorTitle({ provider: o.provider, name: o.vendorName });
  const c = o.contact;
  return (
    <div className="mo-facts">
      <div className="kv">
        <span>Sending addresses</span>
        <span>{kind === o.vendorName ? kind : `${kind}, from ${o.vendorName}`}</span>
      </div>
      {o.domains.map((d) => (
        <div className="kv" key={d.domain}>
          <span>{d.domain}</span>
          <span className="mo-mb">
            {d.mailboxes.map((m) => (
              <span key={m.address}>{m.address}</span>
            ))}
          </span>
        </div>
      ))}
      <div className="kv">
        <span>Leads to</span>
        <span>{o.forwardingDomain}</span>
      </div>
      <div className="kv">
        <span>Registered to</span>
        <span>{[`${c.firstName} ${c.lastName}`.trim(), c.company, c.city].filter(Boolean).join(", ")}</span>
      </div>
    </div>
  );
}

// --- The native panes' parts ---------------------------------------------------------------------------

const errN = (text: string | null | undefined) => (text ? <p className="mo-err mo-pad">{text}</p> : null);

const loading = (words: string) => (
  <div className="pn-grp">
    <div className="pn-row static">
      <span className="mo-spin" aria-hidden="true" />
      <span className="m">
        <span className="t">{words}</span>
      </span>
    </div>
  </div>
);

const inputN = (id: string, label: string, value: string, onChange: (v: string) => void, extra: { auto?: string; type?: string; inputMode?: "tel" | "email" | "text" | "url"; placeholder?: string; invalid?: boolean; raw?: boolean } = {}) => (
  <div className="pn-irow" key={id}>
    <label htmlFor={id}>{label}</label>
    <input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={extra.auto ?? "off"}
      type={extra.type ?? "text"}
      inputMode={extra.inputMode}
      placeholder={extra.placeholder}
      aria-invalid={!!extra.invalid}
      autoCapitalize={extra.raw ? "none" : undefined}
      spellCheck={extra.raw ? false : undefined}
    />
  </div>
);

interface Props {
  productId: string;
  productName: string;
  /** The flow is open from "Buy a domain and sending addresses". */
  open: boolean;
  /** A draft from the orders list to buy: the sheet shows only its Review. */
  draft: MailboxOrder | null;
  orders: MailboxOrder[];
  lastContact: Contact | null;
  onClose: () => void;
  /** The orders changed on the server: read them again. */
  onChanged: () => void;
}

export function BuyMailboxes({ productId, productName, open, draft, orders, lastContact, onClose, onChanged }: Props) {
  const [step, setStep] = useState<Step>("kind");
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [vendorsErr, setVendorsErr] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState<DomainSearch | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [perDomain, setPerDomain] = useState(DEFAULT_PER_DOMAIN);
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [forwarding, setForwarding] = useState("");
  const [contact, setContact] = useState<ContactForm>(EMPTY_CONTACT);
  const [tried, setTried] = useState(false);
  const [review, setReview] = useState<{ key: string; order: MailboxOrder } | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewErr, setReviewErr] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [bought, setBought] = useState<MailboxOrder | null>(null);
  const [pickingCountry, setPickingCountry] = useState(false);
  const native = useNative();
  const prefilled = useRef(false);
  const siteRead = useRef(false);

  const sheetOpen = open || draft !== null;

  const loadVendors = useCallback(() => {
    setVendorsErr(null);
    mailboxOrdersApi.vendors(productId).then(
      (r) => {
        const vs = sortVendors(r.vendors);
        setVendors(vs);
        setVendorId((id) => id ?? vs[0]?.id ?? null);
      },
      (e: unknown) => setVendorsErr(shownError(e)),
    );
  }, [productId]);

  useEffect(() => {
    if (open && !vendors && !vendorsErr) loadVendors();
  }, [open, vendors, vendorsErr, loadVendors]);

  // Why: the domain should lead to the product's own website; the owner's entry wins, then the profile's.
  useEffect(() => {
    if (!open || siteRead.current) return;
    siteRead.current = true;
    void Promise.allSettled([api.products.detail(productId), productsApi.profile(productId)]).then(([d, p]) => {
      const site = (d.status === "fulfilled" ? d.value.product.website : null) || (p.status === "fulfilled" ? p.value.confirmed?.profile.website : null) || "";
      const dom = toDomain(site).replace(/^www\./, "");
      if (dom) setForwarding((f) => f || dom);
    });
  }, [open, productId]);

  useEffect(() => {
    if (prefilled.current || !lastContact) return;
    prefilled.current = true;
    setContact((c) => (CONTACT_KEYS.some((k) => k !== "phoneCc" && c[k]) ? c : contactOf(lastContact)));
  }, [lastContact]);

  const stateOf = (o: MailboxOrder) => (orders.find((x) => x.id === o.id) ?? o).state;

  const resetFlow = () => {
    setPickingCountry(false);
    setStep("kind");
    setQuery("");
    setSearch(null);
    setSearchErr(null);
    setPicked([]);
    setPerDomain(DEFAULT_PER_DOMAIN);
    setCustom({});
    setTried(false);
    setReview(null);
    setReviewErr(null);
    setBought(null);
  };

  // Why: a draft left open in the sheet may have been bought or cancelled from the list meanwhile.
  const lastOpen = useRef(false);
  useEffect(() => {
    if (open && !lastOpen.current && review && stateOf(review.order) !== "draft" && !bought) resetFlow();
    lastOpen.current = open;
  });

  const close = () => {
    if (bought) {
      if (draft) setBought(null);
      else resetFlow();
    }
    setReviewErr(null);
    onClose();
  };

  // --- Domains ---------------------------------------------------------------------------------------

  const find = async (e?: FormEvent) => {
    e?.preventDefault();
    const name = toDomain(query);
    if (!name || vendorId === null) return;
    setSearching(true);
    setSearchErr(null);
    try {
      setSearch(await mailboxOrdersApi.domains(productId, vendorId, name));
    } catch (err) {
      setSearch(null);
      setSearchErr(shownError(err));
    } finally {
      setSearching(false);
    }
  };

  const toggle = (d: string) => setPicked((xs) => (xs.includes(d) ? xs.filter((x) => x !== d) : xs.length >= MAX_DOMAINS ? xs : [...xs, d]));

  // --- Mailboxes ---------------------------------------------------------------------------------------

  const localOf = (d: string, i: number) => custom[`${d}|${i}`] ?? suggestedLocal(first, last, i);

  const mailboxErrors = () => {
    const out: Record<string, string> = {};
    for (const d of picked) {
      const seen = new Set<string>();
      for (let i = 0; i < perDomain; i++) {
        const l = localOf(d, i).trim().toLowerCase();
        if (!LOCAL.test(l) || l.includes("..") || !MailboxDraftSchema.shape.address.safeParse(`${l}@${d}`).success) out[`${d}|${i}`] = "Use letters, numbers, dots or dashes.";
        else if (seen.has(l)) out[`${d}|${i}`] = "This address is already in the list.";
        seen.add(l);
      }
    }
    return out;
  };

  // --- Steps -------------------------------------------------------------------------------------------

  const fwd = toDomain(forwarding);
  const fwdErr = !OrderDraftSchema.shape.forwardingDomain.safeParse(fwd).success
    ? "Use a domain like flashkut.ai."
    : picked.includes(fwd)
      ? "Send visitors to your main website, not to a new domain."
      : null;
  const boxErrs = mailboxErrors();
  const cErrs = contactErrors(contact);

  const stepOk: Record<Step, boolean> = {
    kind: vendorId !== null,
    domains: picked.length > 0,
    mailboxes: !!first.trim() && !!last.trim() && Object.keys(boxErrs).length === 0,
    forwarding: !fwdErr,
    contact: Object.keys(cErrs).length === 0,
    review: true,
  };

  const body = (): OrderDraft => ({
    vendorId: vendorId ?? 0,
    domains: picked.map((d) => ({
      domain: d,
      mailboxes: Array.from({ length: perDomain }, (_, i) => ({ address: `${localOf(d, i).trim().toLowerCase()}@${d}`, firstName: first.trim(), lastName: last.trim() })),
    })),
    forwardingDomain: fwd,
    contact,
  });

  const toReview = async () => {
    const parsed = OrderDraftSchema.safeParse(body());
    setStep("review");
    setReviewErr(null);
    if (!parsed.success) {
      setReviewErr(parsed.error.issues[0]?.message ?? "Some details are missing. Go back and check them.");
      return;
    }
    const key = JSON.stringify(parsed.data);
    const old = review && stateOf(review.order) === "draft" ? review.order : null;
    if (old && review?.key === key) return;
    // Why: an empty key keeps the draft to change next time, but never shows its old price or Buy.
    setReview(old ? { key: "", order: old } : null);
    setReviewBusy(true);
    try {
      const r = old ? await mailboxOrdersApi.change(productId, old.id, parsed.data) : await mailboxOrdersApi.draft(productId, parsed.data);
      setReview({ key, order: r.order });
    } catch (e) {
      setReviewErr(shownError(e));
    } finally {
      setReviewBusy(false);
      onChanged();
    }
  };

  const next = () => {
    if (!stepOk[step]) {
      setTried(true);
      requestAnimationFrame(() => document.querySelector<HTMLElement>(".mo-sheet [aria-invalid=true]")?.focus());
      return;
    }
    setTried(false);
    if (step === "mailboxes" && !contact.firstName && !contact.lastName) setContact((c) => ({ ...c, firstName: first.trim(), lastName: last.trim() }));
    if (step === "contact") void toReview();
    else setStep(STEPS[STEPS.indexOf(step) + 1]);
  };
  const back = () => {
    setTried(false);
    setReviewErr(null);
    setStep(STEPS[Math.max(0, STEPS.indexOf(step) - 1)]);
  };

  const buy = async (o: MailboxOrder) => {
    if (buying) return;
    setBuying(true);
    setReviewErr(null);
    try {
      const r = await mailboxOrdersApi.place(productId, o.id);
      // Why: Smartlead can ask Flashadz to slow down; the order stays a draft and its note says so.
      if (r.order.state === "draft") setReviewErr(r.order.note ?? "Smartlead did not take the order yet. Press Buy again in a minute.");
      else setBought(r.order);
    } catch (e) {
      setReviewErr(shownError(e));
    } finally {
      setBuying(false);
      onChanged();
    }
  };

  // --- Panes -------------------------------------------------------------------------------------------

  const head = (title: string, lede?: string) => {
    const at = STEPS.indexOf(step);
    return (
      <>
        <div className="mo-top">
          <span className="eyebrow">
            Step {at + 1} of {STEPS.length}
          </span>
          <ol className="stages" aria-hidden="true">
            {STEPS.map((s, i) => (
              <li key={s} className={i < at ? "done" : i === at ? "on" : ""} />
            ))}
          </ol>
        </div>
        <h2>{title}</h2>
        {lede && <p className="lede mo-lede">{lede}</p>}
      </>
    );
  };

  const nav = (label = "Continue") => (
    <div className="wk-nav">
      {step !== "kind" && (
        <button type="button" className="btn ghost" onClick={back}>
          <Icon name="left" />
          Back
        </button>
      )}
      <span className="sp">{tried && !stepOk[step] ? "Fill in what is marked first." : ""}</span>
      <button type="button" className="btn" onClick={next} disabled={step === "contact" && reviewBusy}>
        {label}
        <Icon name="right" />
      </button>
    </div>
  );

  const vendorPane = () => (
    <>
      {head("What kind of sending addresses?", "Smartlead buys the domains and sets up the addresses for you. Google is the usual pick for cold email.")}
      {vendorsErr ? (
        <>
          <p className="cx-err">{vendorsErr}</p>
          <button type="button" className="btn wide ghost" onClick={loadVendors}>
            Try again
          </button>
        </>
      ) : !vendors ? (
        <div className="cx-load">
          <span className="spin" />
          <span>Reading what Smartlead sells</span>
        </div>
      ) : vendors.length === 0 ? (
        <p className="cx-err">Smartlead offers no sending addresses right now. Try again later.</p>
      ) : (
        <div className="rows mo-vs" role="radiogroup" aria-label="Kind of sending addresses">
          {vendors.map((v, i) => {
            const when = deliveryWords(v.deliveryHours);
            return (
              <label key={v.id} className="ask-l">
                <input type="radio" name="mo-vendor" checked={vendorId === v.id} onChange={() => setVendorId(v.id)} />
                <span className="ask-t">
                  <b>
                    {vendorTitle(v)}
                    {i === 0 && v.provider === "Google" && <span className="chip auto">Recommended</span>}
                  </b>
                  <span className="mo-vp">
                    {price(v.domainCentsPerYear, v.currency)} a year per domain, {price(v.mailboxCentsPerMonth, v.currency)} a month per address
                  </span>
                  <span>
                    {[when ? `Usually ${when}` : null, v.deliveryRate !== null ? `${Math.round(v.deliveryRate)}% of orders arrive` : null, `From ${v.name}`]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
      {vendors && vendors.length > 0 && nav()}
    </>
  );

  const domainsPane = () => {
    const picks = search ? [...new Set([...(search.available ? [search.domain] : []), ...search.suggestions])] : [];
    const full = picked.length >= MAX_DOMAINS;
    return (
      <>
        {head("Pick the domains", "Cold email goes out from its own domain, so your main one stays safe. Pick a name close to yours.")}
        <form className="mo-find" onSubmit={(e) => void find(e)}>
          <label className="fl" htmlFor="mo-q">
            Domain name
          </label>
          <div className="mo-find-r">
            <input className="field" id="mo-q" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`get${slug(productName) || "yourname"}`} autoComplete="off" autoCapitalize="none" spellCheck={false} />
            <button type="submit" className="btn ghost" disabled={searching || !toDomain(query)}>
              {searching ? "Checking…" : "Check"}
            </button>
          </div>
        </form>
        {searchErr && <p className="cx-err">{searchErr}</p>}
        {search && (
          <div className="mo-found">
            <p className={`mo-avail ${search.available ? "ok" : "bad"}`}>
              <b>{search.domain}</b> {search.available ? "is free." : "is taken."}
              {picks.length > 0 && (search.available ? " Pick the ones you want." : " These are free:")}
            </p>
            {picks.length > 0 ? (
              <div className="tgls mo-picks">
                {picks.map((d) => {
                  const on = picked.includes(d);
                  return (
                    <button key={d} type="button" className={on ? "on" : ""} aria-pressed={on} disabled={!on && full} onClick={() => toggle(d)}>
                      <Icon name={on ? "check" : "plus"} />
                      {d}
                    </button>
                  );
                })}
              </div>
            ) : (
              !search.available && <p className="mo-hint">Try another name.</p>
            )}
          </div>
        )}
        <div className="mo-picked-h">
          <span className="fl">Your domains</span>
          <span className="mo-hint">Up to {MAX_DOMAINS}</span>
        </div>
        {picked.length === 0 ? (
          <p className={`mo-hint${tried ? " mo-err" : ""}`}>None yet. Check a name, then pick it.</p>
        ) : (
          <div className="mo-picked">
            {picked.map((d) => (
              <span key={d} className="chip">
                {d}
                <button type="button" aria-label={`Remove ${d}`} onClick={() => toggle(d)}>
                  <Icon name="x" />
                </button>
              </span>
            ))}
          </div>
        )}
        {nav()}
      </>
    );
  };

  const mailboxesPane = () => (
    <>
      {head("Who signs the emails?", "The name people see on each email. Every address gets it.")}
      <div className="row2">
        <div>
          <label className="fl" htmlFor="mo-first">
            First name
          </label>
          <input className="field" id="mo-first" value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" aria-invalid={tried && !first.trim()} />
          {tried && !first.trim() && <p className="mo-err">Fill this in.</p>}
        </div>
        <div>
          <label className="fl" htmlFor="mo-last">
            Last name
          </label>
          <input className="field" id="mo-last" value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" aria-invalid={tried && !last.trim()} />
          {tried && !last.trim() && <p className="mo-err">Fill this in.</p>}
        </div>
      </div>
      <span className="fl" id="mo-per">
        Addresses per domain
      </span>
      <div className="tgls" role="group" aria-labelledby="mo-per">
        {Array.from({ length: MAX_MAILBOXES_PER_DOMAIN }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" className={perDomain === n ? "on" : ""} aria-pressed={perDomain === n} onClick={() => setPerDomain(n)}>
            {n}
          </button>
        ))}
      </div>
      <p className="mo-hint">Each new address starts at {NEW_MAILBOX.maxPerDay} emails a day.</p>
      {picked.map((d) => (
        <div key={d} className="mo-dom">
          <span className="fl">Addresses on {d}</span>
          {Array.from({ length: perDomain }, (_, i) => {
            const k = `${d}|${i}`;
            const bad = (tried || custom[k] !== undefined) && !!boxErrs[k] && !!localOf(d, i);
            return (
              <div key={k}>
                <div className="mo-addr">
                  <input
                    className="field"
                    value={localOf(d, i)}
                    onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value.replace(/\s/g, "").toLowerCase() }))}
                    aria-label={`Address ${i + 1} on ${d}`}
                    aria-invalid={bad}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                  <span>@{d}</span>
                </div>
                {bad && <p className="mo-err">{boxErrs[k]}</p>}
              </div>
            );
          })}
        </div>
      ))}
      {nav()}
    </>
  );

  const forwardingPane = () => (
    <>
      {head("Where the domain leads", `People who open ${picked[0] ?? "the new domain"} in a browser go to this website. Use your main one.`)}
      <label className="fl" htmlFor="mo-fwd">
        Your website
      </label>
      <input className="field" id="mo-fwd" value={forwarding} onChange={(e) => setForwarding(e.target.value)} placeholder="flashkut.ai" autoComplete="off" autoCapitalize="none" spellCheck={false} aria-invalid={(tried || !!forwarding) && !!fwdErr} />
      {(tried || forwarding) && fwdErr && <p className="mo-err">{fwdErr}</p>}
      {nav()}
    </>
  );

  const field = (k: keyof Contact, label: string, auto: string, extra?: { placeholder?: string; type?: string; inputMode?: "tel" | "email" | "text" }) => (
    <div>
      <label className="fl" htmlFor={`mo-c-${k}`}>
        {label}
      </label>
      <input
        className="field"
        id={`mo-c-${k}`}
        value={contact[k]}
        onChange={(e) => setContact((c) => ({ ...c, [k]: e.target.value }))}
        autoComplete={auto}
        type={extra?.type ?? "text"}
        inputMode={extra?.inputMode}
        placeholder={extra?.placeholder}
        aria-invalid={tried && !!cErrs[k]}
      />
      {tried && cErrs[k] && <p className="mo-err">{cErrs[k]}</p>}
    </div>
  );

  const contactPane = () => (
    <>
      {head("Registration contact", "Every domain needs an owner on record. Smartlead registers yours with these details.")}
      <div className="row2">
        {field("firstName", "First name", "given-name")}
        {field("lastName", "Last name", "family-name")}
      </div>
      <div className="row2">
        {field("email", "Email", "email", { type: "email", inputMode: "email" })}
        {field("company", "Company", "organization")}
      </div>
      {field("addressLineOne", "Address", "address-line1")}
      {field("addressLineTwo", "Address line 2 (optional)", "address-line2")}
      <div className="row2">
        {field("city", "City", "address-level2")}
        {field("state", "State or region", "address-level1")}
      </div>
      <div className="row2">
        {field("postalCode", "Postal code", "postal-code")}
        {field("country", "Country", "country-name", { placeholder: "United States" })}
      </div>
      <div className="mo-phone">
        {field("phoneCc", "Code", "tel-country-code", { inputMode: "tel", placeholder: "+1" })}
        {field("phone", "Phone", "tel-national", { type: "tel", inputMode: "tel" })}
      </div>
      {nav("Check the price")}
    </>
  );

  const reviewPane = (o: MailboxOrder | null) => (
    <>
      {draft ? (
        <>
          <div className="eyebrow">Buy</div>
          <h2>Check and buy</h2>
        </>
      ) : (
        head("Check and buy")
      )}
      {reviewBusy && (
        <div className="cx-load">
          <span className="spin" />
          <span>Checking the order with Smartlead</span>
        </div>
      )}
      {o && (
        <>
          <OrderFacts o={o} />
          <p className="mo-price">{priceSentence(o)}</p>
        </>
      )}
      {reviewErr && <p className="cx-err">{reviewErr}</p>}
      <div className="wk-nav">
        {!draft && (
          <button type="button" className="btn ghost" onClick={back} disabled={buying}>
            <Icon name="left" />
            Back
          </button>
        )}
        <span className="sp" />
        <button type="button" className="btn" disabled={!o || buying || reviewBusy || stateOf(o) !== "draft"} onClick={() => o && void buy(o)}>
          {buying ? "Buying…" : "Buy"}
        </button>
      </div>
      <p className="fine">Smartlead charges the card on your Smartlead account. Flashadz never sees it.</p>
    </>
  );

  const boughtPane = (o: MailboxOrder) => {
    const when = deliveryWords(o.deliveryHours);
    if (o.state === "failed") {
      return (
        <>
          <div className="eyebrow">Not bought</div>
          <h2>Smartlead refused the order</h2>
          <p className="cx-err">{o.note ?? "Smartlead did not say why. Nothing was charged."}</p>
          <button type="button" className="btn wide ghost" onClick={close}>
            Close
          </button>
        </>
      );
    }
    const sure = o.state === "placed" || o.state === "ready";
    return (
      <>
        <div className="eyebrow">{sure ? "Bought" : "Buying"}</div>
        <h2>{sure ? "Your sending addresses are on the way" : "Checking with Smartlead"}</h2>
        {!sure && <p className="lede mo-lede">{o.note ?? "Smartlead's answer was not clear yet. Flashadz checks that the order went through and shows it under the connection."}</p>}
        <p className="lede mo-lede">
          Smartlead is setting up your addresses.{when ? ` This usually takes ${when}.` : ""} Flashadz sets each one to {NEW_MAILBOX.maxPerDay} emails a day when they arrive, so it warms up slowly.
        </p>
        <div className="mo-why">
          <Icon name="shield" />
          <p>
            <b>No warm-up tool, on purpose.</b> Those tools trade fake emails between addresses, and blocklists now flag domains that use them. New addresses warm up by sending real emails, a few a day.
          </p>
        </div>
        <button type="button" className="btn wide" onClick={close}>
          Done
        </button>
      </>
    );
  };

  const pane = () => {
    if (bought) return boughtPane(bought);
    if (draft) return reviewPane(draft);
    if (step === "kind") return vendorPane();
    if (step === "domains") return domainsPane();
    if (step === "mailboxes") return mailboxesPane();
    if (step === "forwarding") return forwardingPane();
    if (step === "contact") return contactPane();
    return reviewPane(review?.key ? review.order : null);
  };

  // --- On a phone and an iPad: one column in a full-height sheet, Back and Continue kept at the bottom ---

  if (native) {
    const at = STEPS.indexOf(step);
    const stepsBar = (
      <div className="mo-steps">
        <span>
          Step {at + 1} of {STEPS.length}
        </span>
        <span className="mo-prog" aria-hidden="true">
          <i style={{ width: `${Math.round(((at + 1) / STEPS.length) * 100)}%` }} />
        </span>
      </div>
    );
    const headN = (title: string, lede?: string) => (
      <>
        {stepsBar}
        <div className="mo-bh">
          <h2>{title}</h2>
          {lede && <p>{lede}</p>}
        </div>
      </>
    );
    const navN = (label = "Continue") => (
      <>
        {tried && !stepOk[step] && <p className="mo-fnote mo-errt">Fill in what is marked first.</p>}
        <div className="pn-btns">
          {step !== "kind" && (
            <button type="button" className="pn-btn mo-bk" onClick={back}>
              <Icon name="left" />
              Back
            </button>
          )}
          <button type="button" className="pn-btn pri" onClick={next} disabled={step === "contact" && reviewBusy}>
            {label}
          </button>
        </div>
      </>
    );
    let content = null;
    let foot = null;
    if (pickingCountry) {
      content = (
        <div className="pn-stack tight">
          <div className="mo-bh">
            <h2>Country</h2>
          </div>
          <PickList
            items={COUNTRIES.map((c) => ({ value: c.name, label: c.name }))}
            value={contact.country}
            label="Country"
            onPick={(v) => {
              setContact((c) => ({ ...c, country: v }));
              setPickingCountry(false);
            }}
          />
        </div>
      );
      foot = (
        <button type="button" className="pn-btn wide" onClick={() => setPickingCountry(false)}>
          Back
        </button>
      );
    } else if (bought) {
      const when = deliveryWords(bought.deliveryHours);
      if (bought.state === "failed") {
        content = (
          <div className="mo-cn">
            <span className="pn-ico danger">
              <Icon name="x" />
            </span>
            <h2>Smartlead refused the order</h2>
            <p>{bought.note ?? "Smartlead did not say why. Nothing was charged."}</p>
          </div>
        );
        foot = (
          <button type="button" className="pn-btn wide" onClick={close}>
            Close
          </button>
        );
      } else {
        const sure = bought.state === "placed" || bought.state === "ready";
        content = (
          <div className="pn-stack tight">
            <div className="mo-cn">
              <span className="pn-ico good">
                <Icon name="check" />
              </span>
              <h2>{sure ? "Your sending addresses are on the way" : "Checking with Smartlead"}</h2>
              {!sure && <p>{bought.note ?? "Smartlead's answer was not clear yet. Flashadz checks that the order went through and shows it under the connection."}</p>}
              <p>
                Smartlead is setting up your addresses.{when ? ` This usually takes ${when}.` : ""} Flashadz sets each one to {NEW_MAILBOX.maxPerDay} emails a day when they arrive, so it warms up slowly.
              </p>
            </div>
            <div className="pn-card mo-why">
              <span className="pn-ico good">
                <Icon name="shield" />
              </span>
              <p>
                <b>No warm-up tool, on purpose.</b> Those tools trade fake emails between addresses, and blocklists now flag domains that use them. New addresses warm up by sending real emails, a few a day.
              </p>
            </div>
          </div>
        );
        foot = (
          <button type="button" className="pn-btn pri wide" onClick={close}>
            Done
          </button>
        );
      }
    } else if (draft || step === "review") {
      const o = draft ?? (review?.key ? review.order : null);
      const c = o?.contact;
      const kind = o ? vendorTitle({ provider: o.provider, name: o.vendorName }) : "";
      content = (
        <>
          {draft ? (
            <div className="mo-bh">
              <h2>Check and buy</h2>
            </div>
          ) : (
            headN("Check and buy")
          )}
          {reviewBusy && loading("Checking the order with Smartlead")}
          {o && c && (
            <>
              <div className="pn-grp mo-facts">
                <div className="pn-row static mo-sum">
                  <span className="m">
                    <span className="t">Sending addresses</span>
                    <span className="s">{kind === o.vendorName ? kind : `${kind}, from ${o.vendorName}`}</span>
                  </span>
                </div>
                {o.domains.map((d) => (
                  <div className="pn-row static mo-sum" key={d.domain}>
                    <span className="m">
                      <span className="t">{d.domain}</span>
                      {d.mailboxes.map((m) => (
                        <span className="s" key={m.address}>
                          {m.address}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
                <div className="pn-row static mo-sum">
                  <span className="m">
                    <span className="t">Leads to</span>
                    <span className="s">{o.forwardingDomain}</span>
                  </span>
                </div>
                <div className="pn-row static mo-sum">
                  <span className="m">
                    <span className="t">Registered to</span>
                    <span className="s">{[`${c.firstName} ${c.lastName}`.trim(), c.company, c.city].filter(Boolean).join(", ")}</span>
                  </span>
                </div>
              </div>
              <div className="pn-card mo-pricec">{priceSentence(o)}</div>
            </>
          )}
          {errN(reviewErr)}
        </>
      );
      foot = (
        <>
          <div className="pn-btns">
            {!draft && (
              <button type="button" className="pn-btn mo-bk" onClick={back} disabled={buying}>
                <Icon name="left" />
                Back
              </button>
            )}
            <button type="button" className="pn-btn pri" disabled={!o || buying || reviewBusy || stateOf(o) !== "draft"} onClick={() => o && void buy(o)}>
              {buying ? "Buying…" : "Buy"}
            </button>
          </div>
          <p className="mo-fnote">Smartlead charges the card on your Smartlead account. Flashadz never sees it.</p>
        </>
      );
    } else if (step === "kind") {
      content = (
        <>
          {headN("What kind of sending addresses?", "Smartlead buys the domains and sets up the addresses for you. Google is the usual pick for cold email.")}
          {vendorsErr ? (
            <>
              {errN(vendorsErr)}
              <div className="mo-pad">
                <button type="button" className="pn-btn soft wide" onClick={loadVendors}>
                  Try again
                </button>
              </div>
            </>
          ) : !vendors ? (
            loading("Reading what Smartlead sells")
          ) : vendors.length === 0 ? (
            errN("Smartlead offers no sending addresses right now. Try again later.")
          ) : (
            <div className="pn-grp" role="radiogroup" aria-label="Kind of sending addresses">
              {vendors.map((v, i) => {
                const on = vendorId === v.id;
                const when = deliveryWords(v.deliveryHours);
                return (
                  <button key={v.id} type="button" role="radio" aria-checked={on} className="pn-row wrap mo-kr" onClick={() => setVendorId(v.id)}>
                    <span className={on ? "mo-radio on" : "mo-radio"}>{on && <Icon name="check" />}</span>
                    <span className="m">
                      <span className="t">
                        {vendorTitle(v)}
                        {i === 0 && v.provider === "Google" && <span className="pn-pill acc">Recommended</span>}
                      </span>
                      <span className="s mo-ink">
                        {price(v.domainCentsPerYear, v.currency)} a year per domain, {price(v.mailboxCentsPerMonth, v.currency)} a month per address
                      </span>
                      <span className="s">{[when ? `Usually ${when}` : null, v.deliveryRate !== null ? `${Math.round(v.deliveryRate)}% of orders arrive` : null, `From ${v.name}`].filter(Boolean).join(" · ")}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      );
      if (vendors && vendors.length > 0) foot = navN();
    } else if (step === "domains") {
      const found = search ? [...new Set([...(search.available ? [search.domain] : []), ...search.suggestions])] : [];
      const rows = [...new Set([...picked, ...found])];
      const full = picked.length >= MAX_DOMAINS;
      content = (
        <>
          {headN("Pick the domains", "Cold email goes out from its own domain, so your main one stays safe. Pick a name close to yours.")}
          <form className="pn-grp mo-form" onSubmit={(e) => void find(e)}>
            <div className="pn-irow mo-dq">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`get${slug(productName) || "yourname"}`} aria-label="Domain name" autoComplete="off" autoCapitalize="none" spellCheck={false} enterKeyHint="search" />
              <button type="submit" className="mo-tl" disabled={searching || !toDomain(query)}>
                {searching ? "Checking…" : "Check"}
              </button>
            </div>
          </form>
          {errN(searchErr)}
          {rows.length > 0 && (
            <section>
              <div className="pn-cap mo-avail">
                {search ? (search.available ? `${search.domain} is free. Pick the ones you want.` : found.length ? `${search.domain} is taken. These are free:` : `${search.domain} is taken.`) : "Your domains"}
              </div>
              <div className="pn-grp">
                {rows.map((d) => {
                  const on = picked.includes(d);
                  return (
                    <button key={d} type="button" className="pn-row" aria-pressed={on} disabled={!on && full} onClick={() => toggle(d)}>
                      <span className={on ? "mo-chk on" : "mo-chk"}>
                        <Icon name="check" />
                      </span>
                      <span className="m">
                        <span className="t">{d}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="pn-note">
                Up to {MAX_DOMAINS}. {picked.length ? `${picked.length} picked.` : "None picked yet."}
              </p>
            </section>
          )}
          {search && rows.length === 0 && <p className="pn-note">Try another name.</p>}
          {tried && picked.length === 0 && errN("Check a name, then pick it.")}
        </>
      );
      foot = navN();
    } else if (step === "mailboxes") {
      content = (
        <>
          {headN("Who signs the emails?", "The name people see on each email. Every address gets it.")}
          <div className="pn-grp mo-form">
            {inputN("mo-first", "First name", first, setFirst, { auto: "given-name", invalid: tried && !first.trim() })}
            {inputN("mo-last", "Last name", last, setLast, { auto: "family-name", invalid: tried && !last.trim() })}
          </div>
          <section>
            <div className="pn-cap">Addresses per domain</div>
            <Segmented
              value={String(perDomain)}
              options={Array.from({ length: MAX_MAILBOXES_PER_DOMAIN }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
              onChange={(v) => setPerDomain(Number(v))}
              label="Addresses per domain"
            />
            <p className="pn-note">Each new address starts at {NEW_MAILBOX.maxPerDay} emails a day.</p>
          </section>
          {picked.map((d) => {
            const errs = Array.from({ length: perDomain }, (_, i) => `${d}|${i}`).filter((k, i) => (tried || custom[k] !== undefined) && !!boxErrs[k] && !!localOf(d, i));
            return (
              <section key={d}>
                <div className="pn-cap">Addresses on {d}</div>
                <div className="pn-grp mo-form">
                  {Array.from({ length: perDomain }, (_, i) => {
                    const k = `${d}|${i}`;
                    return (
                      <div className="pn-irow mo-addr" key={k}>
                        <input
                          value={localOf(d, i)}
                          onChange={(e) => setCustom((c) => ({ ...c, [k]: e.target.value.replace(/\s/g, "").toLowerCase() }))}
                          aria-label={`Address ${i + 1} on ${d}`}
                          aria-invalid={errs.includes(k)}
                          autoComplete="off"
                          autoCapitalize="none"
                          spellCheck={false}
                        />
                        <span className="mo-suf">@{d}</span>
                      </div>
                    );
                  })}
                </div>
                {errs.length > 0 && errN(boxErrs[errs[0]])}
              </section>
            );
          })}
        </>
      );
      foot = navN();
    } else if (step === "forwarding") {
      content = (
        <>
          {headN("Where the domain leads", `People who open ${picked[0] ?? "the new domain"} in a browser go to this website. Use your main one.`)}
          <div className="pn-grp mo-form">{inputN("mo-fwd", "Website", forwarding, setForwarding, { inputMode: "url", placeholder: "flashkut.ai", invalid: (tried || !!forwarding) && !!fwdErr, raw: true })}</div>
          {(tried || forwarding) && errN(fwdErr)}
        </>
      );
      foot = navN();
    } else {
      const bad = (k: keyof Contact) => tried && !!cErrs[k];
      const set = (k: keyof Contact) => (v: string) => setContact((c) => ({ ...c, [k]: v }));
      const specific = (["email", "phoneCc", "phone"] as const).filter((k) => bad(k)).map((k) => cErrs[k]);
      content = (
        <>
          {headN("Registration contact", "Every domain needs an owner on record. Smartlead registers yours with these details.")}
          <div className="pn-grp mo-form">
            {inputN("mo-c-firstName", "First name", contact.firstName, set("firstName"), { auto: "given-name", invalid: bad("firstName") })}
            {inputN("mo-c-lastName", "Last name", contact.lastName, set("lastName"), { auto: "family-name", invalid: bad("lastName") })}
            {inputN("mo-c-email", "Email", contact.email, set("email"), { auto: "email", type: "email", inputMode: "email", invalid: bad("email"), raw: true })}
            {inputN("mo-c-company", "Company", contact.company, set("company"), { auto: "organization", invalid: bad("company") })}
          </div>
          <div className="pn-grp mo-form">
            {inputN("mo-c-addressLineOne", "Address", contact.addressLineOne, set("addressLineOne"), { auto: "address-line1", invalid: bad("addressLineOne") })}
            {inputN("mo-c-addressLineTwo", "Line 2", contact.addressLineTwo, set("addressLineTwo"), { auto: "address-line2", placeholder: "Optional" })}
            {inputN("mo-c-city", "City", contact.city, set("city"), { auto: "address-level2", invalid: bad("city") })}
            {inputN("mo-c-state", "State", contact.state, set("state"), { auto: "address-level1", invalid: bad("state") })}
            {inputN("mo-c-postalCode", "Postal code", contact.postalCode, set("postalCode"), { auto: "postal-code", invalid: bad("postalCode") })}
            <button type="button" className="pn-irow mo-irb" aria-invalid={bad("country")} onClick={() => setPickingCountry(true)}>
              <span className="mo-irl">Country</span>
              <span className={contact.country ? "mo-irv" : "mo-irv mo-dim"}>{contact.country || "Choose"}</span>
              <Icon name="right" />
            </button>
          </div>
          <div className="pn-grp mo-form">
            <div className="pn-irow">
              <label htmlFor="mo-c-phone">Phone</label>
              <input className="mo-ccin" value={contact.phoneCc} onChange={(e) => set("phoneCc")(e.target.value)} aria-label="Country code" autoComplete="tel-country-code" inputMode="tel" aria-invalid={bad("phoneCc")} />
              <input id="mo-c-phone" value={contact.phone} onChange={(e) => set("phone")(e.target.value)} autoComplete="tel-national" type="tel" inputMode="tel" aria-invalid={bad("phone")} />
            </div>
          </div>
          {specific.map((m) => (
            <p className="mo-err mo-pad" key={m}>
              {m}
            </p>
          ))}
        </>
      );
      foot = navN("Check the price");
    }

    return (
      <PnSheet
        open={sheetOpen}
        onClose={close}
        tall
        title="Buy sending addresses"
        label="Buy a domain and sending addresses"
        left={
          <button type="button" className="pn-tbtn" onClick={close}>
            Cancel
          </button>
        }
        foot={foot}
        className="mo-sheet"
      >
        <div className="mo-buy">{content}</div>
      </PnSheet>
    );
  }

  return (
    <Sheet open={sheetOpen} onClose={close} label="Buy a domain and sending addresses" className="mo-sheet">
      {pane()}
    </Sheet>
  );
}
